import React, { useState } from 'react';
import { ModalStatusType } from '../../../types/common';
import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import _ from 'lodash';
import {
  useGetSandboxById,
  useGetSandboxOptionType,
  useGetSandboxTenant,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { SWITCH_SANDBOX_VALIDATION } from '../../../utils/validations/validations';
import { SandboxService } from '../../../services/wealthlane-identity-services';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import InputText from '../../../components/input-text/inputText';
import CustomTextArea from '../../../components/text-area/textArea';
import FormSelect from '../../../components/dropdown/formSelect';
import { commonValidation } from '../../../utils/validations/validationRule';
import * as Yup from 'yup';

interface SwitchSandboxModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  rowData?: any;
  domId?: string;
}

const SwitchSandboxModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
  rowData,
  domId,
}: SwitchSandboxModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: sandboxOptionType, isLoading: isSandboxOptionTypeLoading } =
    useGetSandboxOptionType(handleRequest);
  const { data: sandboxTenantOption, isLoading: isSandboxTenantOption } =
    useGetSandboxTenant(handleRequest);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  const { data: sandboxDetails } = useGetSandboxById(handleRequest, rowData.id);

  const handleSubmit = (values: any): void => {
    setLoading(true);
    values?.id
      ? handleRequest(SandboxService.putApiIdentityApiSandbox(values?.id, values))
        .then((data: any) => {
          if (data) {
            toast.success(<ToastComponent label='Success' message='Sandbox edited.' id='successToast' />);
            queryClient.invalidateQueries('getSwitchSandboxList');
            setLoading(false);
            closeModal();
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        })
      : handleRequest(SandboxService.postApiIdentityApiSandbox(values))
        .then((data: any) => {
          if (data) {
            toast.success(<ToastComponent label='Success' message='Sandbox created.' id='successToast' />);
            queryClient.invalidateQueries('getSwitchSandboxList');
            setLoading(false);
            closeModal();
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={sandboxDetails ? sandboxDetails : initialData.data}
      validationSchema={
        modalStatus === 'Add'
          ? SWITCH_SANDBOX_VALIDATION
          : Yup.object().shape({
            name: commonValidation({
              required: true,
              noBlank: true,
            }),
          })
      }
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading || loader}
            size='sm'
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Sandbox`}
            valueEdited={_.isEqual(
              { name: values?.name, description: values?.description },
              { name: initialData.data?.name, description: initialData.data?.description }
            )}
            id={domId}
            children={
              <Form id='sandboxForm'>
                <ModalBodyComponent id='modalBody'>
                  <InputText
                    type='text'
                    label='Name'
                    name='name'
                    id='name'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    // inputType={initialData.modalStatus}
                    // disabled={initialData.modalStatus === 'View'}
                    required
                  />
                  <div>
                    <FormSelect
                      type='text'
                      label='Type'
                      name='sandboxType'
                      id='sandboxType'
                      viewName='sandboxType'
                      onChange={(e: any) => {
                        // if (sandboxOptionType?.map((item) => item?.label !== 'Copy')) {
                        //   // Only allow selection if option is not "Copy"
                        //   setFieldValue('sandboxType', e);
                        // }
                        setFieldValue('sandboxType', e);
                        setFieldValue(
                          'sandboxTypeLabel',
                          sandboxOptionType?.find((item) => item?.value === e)?.label
                        );
                        setFieldValue('fromTenantId', null);
                        handleChange(e);
                      }}
                      errors={errors}
                      touched={touched}
                      options={sandboxOptionType}
                      isLoading={isSandboxOptionTypeLoading}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View' || modalStatus === 'Edit'}
                      required
                    />
                  </div>
                  <div>
                    <FormSelect
                      type='text'
                      label='From'
                      name='fromTenantId'
                      id='fromTenantId'
                      viewName='fromTenantId'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      options={sandboxTenantOption}
                      isLoading={isSandboxTenantOption}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={values.sandboxTypeLabel !== 'Copy' || modalStatus === 'Edit'}
                      required
                    />
                  </div>
                  <CustomTextArea
                    label='Description'
                    rows='5'
                    name='description'
                    id='description'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                  // disabled={initialData.modalStatus === 'View'}
                  />
                </ModalBodyComponent>
                <ModalFooterComponent
                  modalStatus={modalStatus}
                  loading={loading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(
                    { name: values?.name, description: values?.description },
                    { name: initialData.data?.name, description: initialData.data?.description }
                  )}
                  id='modalFooter'
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default SwitchSandboxModal;
